import { Config } from '../../video-data/config';
import { ErrorReporter } from '../../libs/error-reporter';
import type { Asset, MorphParameters } from '../asset/BaseAsset';
import type { Scene } from '../scene/Scene';
import type { SafeLuminary } from '../../index';
import { copyAssetToContainerAsSticker } from './copyAssetAsSticker';
import type { MorphPairing } from './matchCutPairing';
import {
    assetsAtBoundary,
    deriveMatchCutPairings,
    getTextMorphUnits,
    resolveMatchCutPairs,
} from './matchCutPairing';

const MATCH_CUT_MORPH_INTERPOLATION = Config.EASE_IN_OUT_INTERPOLATION_PRESET;

type MatchCutDuplicatePlacement = { original: Asset; positionSource: Asset };
const matchCutDuplicatePlacements = new WeakMap<Asset, MatchCutDuplicatePlacement>();
const realignArmedLuminaries = new WeakSet<SafeLuminary>();

function canFreezeNonTextForMatchCut(asset: Asset): boolean {
    return (
        Config.isMediaAsset(asset) ||
        Config.isIconAsset(asset) ||
        Config.isChartAsset(asset) ||
        Config.isColorAsset(asset) ||
        Config.isRectangleAsset(asset) ||
        Config.isCircleAsset(asset) ||
        Config.isGradientAsset(asset)
    );
}

function initMatchCutDuplicate(duplicate: Asset): void {
    duplicate.opacity = 0;
    duplicate.isMatchCutDuplicate = true;
}

// Restore createdFrom so a full-bleed media duplicate stays centered instead of jumping at the cut.
function restoreMediaDuplicateCreatedFrom(duplicate: Asset, source: Asset): void {
    if (Config.isMediaAsset(duplicate) && Config.isMediaAsset(source)) {
        duplicate._createdFromCustomLoad(source.createdFrom);
    }
}

// Reads original/positionSource bounds, not the duplicate's (unbuilt inside batchRun; force-building breaks the morph).
function placeMorphDuplicate(duplicate: Asset, original: Asset, positionSource: Asset): void {
    const targetBounds = positionSource.getBounds();
    const originalBounds = original.getBounds();
    const { anchor } = original.transformation;

    duplicate.transformation.position.x =
        targetBounds.x +
        targetBounds.width / 2 -
        originalBounds.width / 2 +
        anchor.x * originalBounds.width;
    duplicate.transformation.position.y =
        targetBounds.y +
        targetBounds.height / 2 -
        originalBounds.height / 2 +
        anchor.y * originalBounds.height;
    initMatchCutDuplicate(duplicate);
}

// Clear inherited animations but keep mask.transition (unserialized), or the frozen mask stays clipped.
function freezeDuplicateAnimations(duplicate: Asset): void {
    duplicate.animationConfig = duplicate.animationConfig.filter((config) =>
        config.drivenProperty.includes('mask.transition'),
    );
    if ('objectAnimation' in duplicate) {
        duplicate.objectAnimation = [];
    }
}

function createFrozenDuplicate(assetToFreeze: Asset, targetContainer: Scene): Asset {
    const duplicate = copyAssetToContainerAsSticker(assetToFreeze, targetContainer);
    initMatchCutDuplicate(duplicate);
    freezeDuplicateAnimations(duplicate);
    restoreMediaDuplicateCreatedFrom(duplicate, assetToFreeze);
    return duplicate;
}

// Add nextScene's assets first so the duplicates stack in the same order they do in nextScene.
function freezeAssetsAcrossCut(
    scene: Scene,
    nextScene: Scene,
    sourceAssets: Asset[],
    targetAssets: Asset[],
): void {
    targetAssets.forEach((targetAsset) => {
        const duplicate = createFrozenDuplicate(targetAsset, scene);
        duplicate.morphAlphaOnly = true;
        duplicate.morphAlphaStep = true;
    });

    sourceAssets.forEach((sourceAsset) => {
        createFrozenDuplicate(sourceAsset, nextScene);
        sourceAsset.morphAlphaOnly = true;
        sourceAsset.morphAlphaStep = true;
    });
}

function freezeNonTextAcrossCut(scene: Scene, nextScene: Scene): void {
    // Media duplicates lose their sticker flag via restoreMediaDuplicateCreatedFrom, so gate on isMatchCutDuplicate too.
    const freezable = (asset: Asset) =>
        !asset.isMatchCutDuplicate &&
        !Config.isStickerAsset(asset) &&
        canFreezeNonTextForMatchCut(asset);
    freezeAssetsAcrossCut(
        scene,
        nextScene,
        assetsAtBoundary(scene, 'end').filter(freezable),
        assetsAtBoundary(nextScene, 'start').filter(freezable),
    );
}

function freezeUnmatchedTextAcrossCut(
    scene: Scene,
    nextScene: Scene,
    pairings: MorphPairing[],
): void {
    const pairedUnitIds = new Set(
        pairings.flatMap((pairing) => [pairing.sourceId, pairing.targetId]),
    );
    const unmatched = (units: Asset[]) => units.filter((unit) => !pairedUnitIds.has(unit.id));
    freezeAssetsAcrossCut(
        scene,
        nextScene,
        unmatched(getTextMorphUnits(scene, 'end')),
        unmatched(getTextMorphUnits(nextScene, 'start')),
    );
}

// Callers must wrap this in luminary.batchRun — it mutates many assets and would otherwise render each.
export function duplicateMatchCutAssets(
    scene: Scene,
    nextScene: Scene,
    pairings: MorphPairing[],
): void {
    const textPairs = resolveMatchCutPairs(scene, nextScene, pairings);
    if (textPairs.length === 0) {
        return;
    }

    const stickerIdsBeforeDuplication = new Set(
        [scene, nextScene].flatMap((target) =>
            target.stickers.getAssets().map((asset) => asset.id),
        ),
    );

    // Freeze the non-text frame first — stickers stack in creation order, so text duplicates render on top.
    freezeNonTextAcrossCut(scene, nextScene);

    textPairs.forEach(({ sourceAsset, targetAsset }) => {
        const targetSideDuplicate = copyAssetToContainerAsSticker(sourceAsset, nextScene);
        const sceneSideDuplicate = copyAssetToContainerAsSticker(targetAsset, scene);
        placeMorphDuplicate(targetSideDuplicate, sourceAsset, targetAsset);
        placeMorphDuplicate(sceneSideDuplicate, targetAsset, sourceAsset);
        matchCutDuplicatePlacements.set(targetSideDuplicate, {
            original: sourceAsset,
            positionSource: targetAsset,
        });
        matchCutDuplicatePlacements.set(sceneSideDuplicate, {
            original: targetAsset,
            positionSource: sourceAsset,
        });
        sourceAsset.morphInterpolation = MATCH_CUT_MORPH_INTERPOLATION;
        sceneSideDuplicate.morphInterpolation = MATCH_CUT_MORPH_INTERPOLATION;
        sourceAsset.morphAlphaStep = true;
        sceneSideDuplicate.morphAlphaStep = true;
    });

    freezeUnmatchedTextAcrossCut(scene, nextScene, pairings);

    // Mark newly-added stickers (deep, so group children too) non-serializing and non-editable.
    [scene, nextScene].forEach((target) => {
        target.stickers
            .getAssets()
            .filter((sticker) => !stickerIdsBeforeDuplication.has(sticker.id))
            .forEach((sticker) => {
                sticker.isMatchCutDuplicate = true;
                sticker.isEditable = false;
            });
    });

    // isMatchCutFrozen holds entry/exit animations at rest; a morph-paired asset still slides via its own morph.
    [...assetsAtBoundary(scene, 'end'), ...assetsAtBoundary(nextScene, 'start')].forEach(
        (asset) => {
            asset.isMatchCutFrozen = true;
        },
    );
}

function realignMatchCutDuplicates(luminary: SafeLuminary): void {
    const placements = luminary.scenes.flatMap((scene) =>
        scene.stickers.getAssets().flatMap((sticker) => {
            const placement = matchCutDuplicatePlacements.get(sticker);
            if (!placement || placement.original.destroyed || placement.positionSource.destroyed) {
                return [];
            }
            return [{ duplicate: sticker, placement }];
        }),
    );
    if (placements.length === 0) {
        return;
    }
    luminary.batchRun(() => {
        placements.forEach(({ duplicate, placement }) => {
            placeMorphDuplicate(duplicate, placement.original, placement.positionSource);
        });
    });
}

export function hasPersistedMatchCut(luminary: SafeLuminary): boolean {
    return luminary.scenes.some((scene) => scene.matchCutTechnique !== Config.MATCH_CUT_NONE);
}

function hasMatchCutDuplicates(luminary: SafeLuminary): boolean {
    return luminary.scenes.some((scene) =>
        scene.stickers.getAssets().some((sticker) => sticker.isMatchCutDuplicate),
    );
}

// Arm one stateChanged listener that re-places stand-ins when their original moves; only the editor arms it, never the renderer.
// realignMatchCutDuplicates no-ops when there are no stand-ins, so this is safe to call before rehydration runs.
export function armMatchCutDuplicateRealignment(luminary: SafeLuminary): void {
    if (realignArmedLuminaries.has(luminary)) {
        return;
    }
    realignArmedLuminaries.add(luminary);
    luminary.on('stateChanged', () => realignMatchCutDuplicates(luminary));
}

// Recreate the transient (unsaved) match-cut duplicates from persisted morphEnabled/matchCutTechnique.
export function rehydrateMatchCutDuplicates(luminary: SafeLuminary): void {
    if (!luminary.template.transitionsEnabled) return;

    // Whole-pass guard: a morph chain hands scene N+1 its duplicates during scene N's cut, before N+1's turn.
    if (hasMatchCutDuplicates(luminary)) return;

    luminary.scenes.forEach((scene) => {
        const { nextScene } = scene;
        if (!scene.morphEnabled || !nextScene) return;
        if (scene.matchCutTechnique === Config.MATCH_CUT_NONE) return;

        const stickerIdsBefore = matchCutStickerIds(scene, nextScene);
        const morphPropsBefore = snapshotMorphPropsAcrossCut(scene, nextScene);
        // Contain per scene: a bad cut (e.g. an unsupported group child) must not abort the remaining cuts.
        try {
            const pairings = deriveMatchCutPairings(scene, nextScene);
            luminary.batchRun(() => {
                duplicateMatchCutAssets(scene, nextScene, pairings);
            });
        } catch (error) {
            ErrorReporter.capture(error);
            removeStickersAddedSince(scene, nextScene, stickerIdsBefore);
            restoreMorphProps(morphPropsBefore);
        }
    });
}

type MorphPropsSnapshot = { morphParameters: MorphParameters; isMatchCutFrozen: boolean };

// Snapshot so a failed cut restores exactly its prior state, without undoing a neighbouring cut's freeze on a shared asset.
function snapshotMorphPropsAcrossCut(
    scene: Scene,
    nextScene: Scene,
): Map<Asset, MorphPropsSnapshot> {
    return new Map(
        [...assetsAtBoundary(scene, 'end'), ...assetsAtBoundary(nextScene, 'start')]
            .filter((asset) => !asset.isMatchCutDuplicate)
            .map((asset) => [
                asset,
                {
                    morphParameters: asset._morphParameters,
                    isMatchCutFrozen: asset.isMatchCutFrozen,
                },
            ]),
    );
}

function restoreMorphProps(snapshot: Map<Asset, MorphPropsSnapshot>): void {
    snapshot.forEach(({ morphParameters, isMatchCutFrozen }, asset) => {
        asset._morphParameters = morphParameters;
        asset.isMatchCutFrozen = isMatchCutFrozen;
    });
}

function matchCutStickerIds(scene: Scene, nextScene: Scene): Set<string> {
    return new Set(
        [scene, nextScene].flatMap((target) =>
            target.stickers.getAssets({ deep: false }).map((sticker) => sticker.id),
        ),
    );
}

// Only top-level stickers are added directly; destroy(true) cascades to their group children.
function removeStickersAddedSince(scene: Scene, nextScene: Scene, idsBefore: Set<string>): void {
    [scene, nextScene].forEach((target) => {
        target.stickers
            .getAssets({ deep: false })
            .filter((sticker) => !idsBefore.has(sticker.id))
            .forEach((sticker) => {
                target.stickers.removeChild(sticker);
                sticker.destroy(true);
            });
    });
}
